import React, { useState, useEffect } from 'react';
import { Lock, Eye, EyeOff, ArrowLeft, Check, Loader2 } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const PasswordRequirement = ({ isValid, text }) => (
  <div className={`flex items-center gap-2 text-sm ${isValid ? 'text-green-600' : 'text-gray-600'}`}>
    {isValid ? (
      <Check className="h-4 w-4" />
    ) : (
      <div className="h-4 w-4 rounded-full border border-gray-300" />
    )}
    {text}
  </div>
);

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { t } = useTranslation();

  const [passwordChecks, setPasswordChecks] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
    match: false
  });

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      navigate('/login');
    }
  }, [token, navigate]);

  useEffect(() => {
    setPasswordChecks({
      length: newPassword.length >= 8,
      uppercase: /[A-Z]/.test(newPassword),
      lowercase: /[a-z]/.test(newPassword),
      number: /[0-9]/.test(newPassword),
      special: /[@$!%*?&#.,]/.test(newPassword),
      match: newPassword === confirmPassword && newPassword !== ''
    });
  }, [newPassword, confirmPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!Object.values(passwordChecks).every(check => check)) {
      setError(t("auth.reset.error.requirements"));
      return;
    }

    setError('');
    setIsLoading(true);

    const requestBody = {
      token: token,
      newPassword: newPassword
    };

    try {
      const response = await fetch('https://www.ia-schedule.com:4139/api/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || t("auth.reset.error.failed"));
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/login', {
          state: { message: t("auth.reset.success.title") }
        });
      }, 3000);
    } catch (err) {
      if (err.message === 'New password must be different from current password') {
        setError(t("auth.reset.error.different"));
        setNewPassword('');
        setConfirmPassword('');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex flex-col items-center -mt-16 pt-16 bg-gray-100 px-4 sm:px-6 lg:px-8 py-8">
      <div className="max-w-md w-full">
        <Link
          to="/login"
          className="flex items-center gap-2 text-indigo-600 hover:text-indigo-500 mb-6"
        >
          <ArrowLeft size={20} />
          <span>{t("auth.back.toLogin")}</span>
        </Link>

        <div className="text-center">
          <Lock className="mx-auto h-12 w-12 text-indigo-600" />
          <h2 className="mt-4 text-3xl font-extrabold text-gray-900">
            {t("auth.reset.title")}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {t("auth.reset.subtitle")}
          </p>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 text-red-500 rounded-lg text-sm">
            {error}
          </div>
        )}

        {success ? (
          <div className="mt-6 p-4 bg-green-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Check className="h-6 w-6 text-green-500" />
              <div>
                <h3 className="font-medium text-green-800">{t("auth.reset.success.title")}</h3>
                <p className="text-sm text-green-600">{t("auth.reset.success.redirect")}</p>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            <div className="space-y-4">
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                  {t("auth.reset.newPassword")}
                </label>
                <div className="mt-1 relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="appearance-none block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  {t("auth.reset.confirmPassword")}
                </label>
                <div className="mt-1 relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="appearance-none block w-full pl-10 py-3 border border-gray-300 rounded-lg placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">{t("auth.password.title")}</h4>
                <div className="space-y-2">
                  <PasswordRequirement isValid={passwordChecks.length} text={t("auth.password.length")} />
                  <PasswordRequirement isValid={passwordChecks.uppercase} text={t("auth.password.uppercase")} />
                  <PasswordRequirement isValid={passwordChecks.lowercase} text={t("auth.password.lowercase")} />
                  <PasswordRequirement isValid={passwordChecks.number} text={t("auth.password.number")} />
                  <PasswordRequirement isValid={passwordChecks.special} text={t("auth.password.special")} />
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading || !Object.values(passwordChecks).every(check => check)}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin h-5 w-5 mr-2" />
                  {t("auth.reset.resetting")}
                </>
              ) : (
                t("auth.reset.button")
              )}
            </button>
          </form>
        )}
      </div>
    </main>
  );
};

export default ResetPassword;